import React, { useState, useEffect } from 'react';
import { RefreshCw, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as Logo } from "../../../assets/icons/svg/Auth/Logo-with-text.svg";
import { API_URLS } from '../../api/urls';
import AuthUtils from '../../utils/auth.utils';

const SignInPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaVerifying, setCaptchaVerifying] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // Validation states
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [apiError, setApiError] = useState('');

  // Check if user already has a valid session or remembered device
  useEffect(() => {
    if (AuthUtils.isSessionValid()) {
      // User has valid session, redirect to dashboard
      navigate('/dashboard', { replace: true });
    } else if (AuthUtils.isDeviceRemembered()) {
      // Device is remembered, pre-check remember device checkbox
      setRememberDevice(true);
    }
  }, [navigate]);

  // Email validation
  const validateEmail = (value: string): string => {
    if (!value) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  // Password validation
  const validatePassword = (value: string): string => {
    if (!value) {
      return 'Password is required';
    }
    if (value.length < 6) {
      return 'Password must be at least 6 characters';
    }
    return '';
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setApiError('');
    if (emailTouched) {
      setEmailError(validateEmail(value));
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    setEmailError(validateEmail(email));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    setApiError('');
    if (passwordTouched) {
      setPasswordError(validatePassword(value));
    }
  };

  const handlePasswordBlur = () => {
    setPasswordTouched(true);
    setPasswordError(validatePassword(password));
  };

  const handleSubmit = () => {
    // Final validation check
    const emailErr = validateEmail(email);
    const passwordErr = validatePassword(password);
    
    setEmailError(emailErr);
    setPasswordError(passwordErr);
    setEmailTouched(true);
    setPasswordTouched(true);

    if (!emailErr && !passwordErr && captchaVerified) {
      console.log('Proceeding to 2FA with:', { email, password, rememberDevice });
      // Navigate to 2FA page with email and rememberDevice in state
      navigate('/otp_verification', { state: { email, rememberDevice } });
    }
  };

  const handleCaptchaClick = async () => {
    if (captchaVerifying || captchaVerified) return;
    
    // Validate credentials first
    const emailErr = validateEmail(email);
    const passwordErr = validatePassword(password);
    
    setEmailTouched(true);
    setPasswordTouched(true);
    setEmailError(emailErr);
    setPasswordError(passwordErr);
    setApiError('');

    if (emailErr || passwordErr) {
      return;
    }
    
    setCaptchaVerifying(true);
    setProgress(0);
    
    try {
      // Start progress animation
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      // Call the API
      const response = await fetch(API_URLS.ADMIN_LOGING, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password,
        }),
      });

      const data = await response.json();
      
      clearInterval(progressInterval);

      if (data.success === 1) {
        console.log('Login Success:', data);
        
        // Now call VERIFY_CAPTCHA API
        try {
          const captchaResponse = await fetch(API_URLS.VERIFY_CAPACHA, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: email,
              captcha_token: "",
            }),
          });

          const captchaData = await captchaResponse.json();
          
          if (captchaData.success === 1) {
            console.log('Captcha Verification Success:', captchaData);
            setProgress(100);
            setTimeout(() => {
              setCaptchaVerifying(false);
              setCaptchaVerified(true);
            }, 300);
          } else {
            setProgress(0);
            setCaptchaVerifying(false);
            setApiError(captchaData.message || 'Captcha verification failed. Please try again.');
          }
        } catch (captchaError) {
          setProgress(0);
          setCaptchaVerifying(false);
          setApiError('Captcha verification error. Please try again.');
          console.error('Captcha API Error:', captchaError);
        }
      } else {
        setProgress(0);
        setCaptchaVerifying(false);
        setApiError(data.message || 'Invalid credentials. Please try again.');
      }
    } catch (error) {
      setProgress(0);
      setCaptchaVerifying(false);
      setApiError('Network error. Please check your connection and try again.');
      console.error('API Error:', error);
    }
  };

  const isFormValid = !emailError && !passwordError && email && password && captchaVerified;

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      {/* Logo */}
      <div className="absolute top-[47px] left-[66px]">
        <div className="flex items-center gap-2">
            <Logo className="h-8 w-auto" />
        </div>
      </div>

      {/* Login Card */}
      <div className="w-full max-w-[540px] bg-white rounded-2xl border border-[#2F2E38] shadow-md p-6 sm:p-8">
        <h1 className="text-2xl font-semibold text-black mb-8">
          Admin panel login
        </h1>

        <div className="space-y-4">
          {/* Email Input */}
          <div>
            <label className="block text-sm font-normal text-black mb-2">
              Enter registered email
            </label>
            <input
              type="email"
              value={email}
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
              className={`w-full px-4 py-3 bg-white border rounded-lg text-sm text-black placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-black  transition-all ${
                emailError && emailTouched
                  ? 'border-red-500 focus:ring-red-200'
                  : 'border-[#CFCFCF] focus:ring-black'
              }`}
              placeholder="example@email.com"
            />
            {emailError && emailTouched && (
              <p className="mt-1.5 text-xs text-red-500">{emailError}</p>
            )}
          </div>

          {/* Password Input */}
          <div>
            <label className="block text-sm font-normal text-black mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={handlePasswordChange}
                onBlur={handlePasswordBlur}
                className={`w-full px-4 py-3 pr-12 bg-white border rounded-lg text-sm text-black placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-black  transition-all ${
                  passwordError && passwordTouched
                    ? 'border-red-500 focus:ring-red-200'
                    : 'border-[#CFCFCF] focus:ring-black'
                }`}
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {passwordError && passwordTouched && (
              <p className="mt-1.5 text-xs text-red-500">{passwordError}</p>
            )}
          </div>

          {/* Remember Device Checkbox */}
          <div className="flex items-center gap-2">
            <div className="relative flex items-center">
              <input
                type="checkbox"
                id="remember"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                className="w-[18px] h-[18px] rounded border-2 border-gray-400 appearance-none checked:bg-black checked:border-black cursor-pointer"
              />
              {rememberDevice && (
                <svg
                  className="absolute w-[18px] h-[18px] pointer-events-none"
                  viewBox="0 0 18 18"
                  fill="none"
                >
                  <path
                    d="M4 9L7.5 12.5L14 6"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            <label htmlFor="remember" className="text-sm text-gray-600 cursor-pointer select-none">
              Remember this device
            </label>
          </div>

          {/* CAPTCHA */}
          <div>
            <button
              type="button"
              onClick={handleCaptchaClick}
              disabled={captchaVerifying || captchaVerified}
              className={`w-full relative overflow-hidden px-4 py-3 border rounded-lg transition-all ${
                captchaVerified
                  ? 'border-green-500 bg-green-50'
                  : captchaVerifying
                  ? 'border-gray-400 bg-gray-50'
                  : 'border-[#D1D5DB] bg-white hover:border-gray-400'
              } ${captchaVerifying || captchaVerified ? 'cursor-default' : 'cursor-pointer'}`}
            >
              {/* Progress Bar Background */}
              {captchaVerifying && (
                <div 
                  className="absolute inset-0 bg-black transition-all duration-100 ease-linear"
                  style={{ width: `${progress}%` }}
                />
              )}
              
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-medium ${
                    captchaVerified 
                      ? 'text-green-700' 
                      : captchaVerifying 
                      ? 'text-white' 
                      : 'text-black'
                  }`}>
                    {captchaVerified ? 'Verified' : captchaVerifying ? 'Verifying...' : 'Click to verify'}
                  </span>
                </div>
                <RefreshCw className={`w-5 h-5 ${
                  captchaVerified 
                    ? 'text-green-600' 
                    : captchaVerifying 
                    ? 'text-black' 
                    : 'text-black'
                }`} />
              </div>
            </button>
            {apiError && (
              <p className="mt-1.5 text-xs text-red-500">{apiError}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={!isFormValid}
            className={`w-full py-3.5 rounded-lg text-[15px] font-medium transition-all mt-6 ${
              isFormValid
                ? 'bg-black text-white hover:bg-gray-800 cursor-pointer'
                : 'bg-[#E5E5E5] text-gray-400 cursor-not-allowed'
            }`}
          >
            Continue for 2FA
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignInPage;